import { ref, computed, watch } from 'vue'
import { isEqual } from 'lodash-es'

/**
 * Imaging dimension validation for z-stack and time-lapse fields.
 * Returns toggle state and raw error refs — the parent wires these
 * into useAssayVuelidate alongside other errors (e.g. channels).
 *
 * Also handles toggle reset side effects (dv.edit when toggling off).
 *
 * @param {Object} dv - The reactive detail view object
 */
export function useImagingDimensionValidation(dv) {
  const zStack = ref(dv.editedRecord?.z_planes?.value > 1)
  const timeLapse = ref(dv.editedRecord?.time_points?.value > 1)

  const zPlanesError = computed(() => {
    if (!zStack.value) return null
    const val = dv.editedRecord?.z_planes?.value
    return !val || val <= 1 ? 'Should be more than 1' : null
  })

  const timePointsError = computed(() => {
    if (!timeLapse.value) return null
    const val = dv.editedRecord?.time_points?.value
    return val <= 1 ? 'Should be more than 1' : null
  })

  // Reset value when toggling off
  watch(zStack, (on) => {
    if (!on) dv.edit({ name: 'z_planes' }, 1)
  })

  watch(timeLapse, (on) => {
    if (!on) dv.edit({ name: 'time_points' }, 1)
  })

  return {
    zStack,
    timeLapse,
    zPlanesError,
    timePointsError,
  }
}

/**
 * Channel index validation for imaging assays.
 * Checks that channel indexes start from 1 and follow in increasing order.
 *
 * @param {Object} dv - The reactive detail view object
 */
export function useChannelsValidation(dv) {
  const channelsError = computed(() => {
    const channels = dv.editedRecord['channels']?.value
    if (!channels) return null
    const sortedIndexes = [
      ...new Set(channels.map((channel) => Number(channel.number.value))),
    ].sort((a, b) => a - b)
    const expectedIndexes = Array.from(
      { length: sortedIndexes.length },
      (_, index) => index + 1,
    )
    if (!isEqual(sortedIndexes, expectedIndexes)) {
      return 'Channel Index should start from 1 and follow in increasing order without any gaps'
    }
    return null
  })

  return { channelsError }
}
